/**
 * ============================================
 * NUMBER FORMATTERS - MAPASAC ERP
 * ============================================
 * 
 * Funciones universales para formateo de números
 * Compatible con cualquier módulo del sistema
 * 
 * Filosofía: Zero hardcode, configurable, reutilizable
 * Autor: Mapasac Team
 * Licencia: Open Source (para compartir con el mundo)
 * 
 * ============================================
 */

/**
 * Formatea número con separador de miles y decimales partidas
 * 
 * Ejemplos:
 * formatNumber(1234.56, 2) → "1,234.56"
 * formatNumber(1234567, 0) → "1,234,567"
 * formatNumber(99.5, 4) → "99.5000"
 * 
 * @param {number|string} value - Valor a formatear
 * @param {number} decimals - Número de decimales (0-10)
 * @param {object} options - Opciones adicionales
 * @param {string} options.thousandSeparator - Separador de miles (default: ',')
 * @param {string} options.decimalSeparator - Separador decimal (default: '.')
 * @returns {string} - Valor formateado
 */
export const formatNumber = (
  value, 
  decimals = 2, 
  options = {}
) => {
  // Valores por defecto
  const {
    thousandSeparator = ',',
    decimalSeparator = '.'
  } = options;

  // Validar entrada
  if (value === null || value === undefined || value === '') return '';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  // Formatear con decimales especificados
  const formatted = num.toFixed(decimals);
  
  // Separar parte entera y decimal
  const [integerPart, decimalPart] = formatted.split('.');
  
  // Agregar separador de miles cada 3 dígitos
  const withSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  
  // Retornar con decimales si existen
  if (decimalPart && decimals > 0) {
    return `${withSeparator}${decimalSeparator}${decimalPart}`;
  }
  
  return withSeparator;
};

/**
 * Formatea números para tabla de totales
 * Lee el formato desde el schema JSON
 * 
 * @param {number|string} value - Valor a formatear
 * @param {string} format - Formato: 'money', 'numeric', 'percent'
 * @param {number} decimals - Número de decimales
 * @returns {string} - Valor formateado
 */
export const formatTotalNumber = (value, format = 'money', decimals = 2) => {
  // Validar entrada
  if (value === null || value === undefined || value === '') return '0.00';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  
  // Formatear con decimales
  const formatted = num.toFixed(decimals);
  const [integerPart, decimalPart] = formatted.split('.');
  
  // Agregar separador de miles
  const withSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Construir resultado
  const result = decimalPart && decimals > 0 
    ? `${withSeparator}.${decimalPart}`
    : withSeparator;
  
  // Agregar símbolo $ si es money
  if (format === 'money' || format === 'money_with_symbol') {
    return `$${result}`;
  }
  
  return result;
};

/**
 * Formatea número como moneda
 * 
 * Ejemplos:
 * formatCurrency(1234.56) → "$1,234.56"
 * formatCurrency(99, 'MXN') → "$99.00 MXN"
 * formatCurrency(1234.567, 'USD', 4) → "$1,234.5670"
 * 
 * @param {number|string} value - Valor a formatear
 * @param {string} currency - Código de moneda (default: '')
 * @param {number} decimals - Número de decimales (default: 2)
 * @param {object} options - Opciones adicionales
 * @returns {string} - Valor formateado como moneda
 */
export const formatCurrency = (
  value, 
  currency = '', 
  decimals = 2,
  options = {}
) => {
  const formatted = formatNumber(value, decimals, options);
  if (!formatted) return '';
  
  // Símbolo de moneda
  const symbol = '$';
  
  // Con o sin código de moneda
  if (currency) {
    return `${symbol}${formatted} ${currency}`;
  }
  
  return `${symbol}${formatted}`;
};

/**
 * Formatea porcentaje
 * 
 * Ejemplos:
 * formatPercent(0.16) → "16.00%"
 * formatPercent(0.085, 1) → "8.5%"
 * formatPercent(1.5, 0) → "150%"
 * 
 * @param {number} value - Valor decimal (0.16 = 16%)
 * @param {number} decimals - Número de decimales (default: 2)
 * @returns {string} - Valor formateado como porcentaje
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  const percent = num * 100;
  return `${percent.toFixed(decimals)}%`;
};

/**
 * Des-formatea string a número
 * Útil para parsear valores formateados de vuelta a número
 * 
 * Ejemplos:
 * parseFormattedNumber("1,234.56") → 1234.56
 * parseFormattedNumber("$1,234.56") → 1234.56
 * parseFormattedNumber("16.00%") → 16
 * 
 * @param {string} formattedValue - Valor formateado
 * @returns {number} - Valor numérico
 */
export const parseFormattedNumber = (formattedValue) => {
  if (!formattedValue) return 0;
  
  // Remover todo excepto números, punto y signo negativo
  const cleaned = String(formattedValue).replace(/[^\d.-]/g, '');
  
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/**
 * Valida si un string es un número válido
 * 
 * @param {string} value - Valor a validar
 * @returns {boolean} - true si es número válido
 */
export const isValidNumber = (value) => {
  if (value === null || value === undefined || value === '') return false;
  
  const num = parseFloat(value);
  return !isNaN(num) && isFinite(num);
};

/**
 * Redondea número a N decimales (sin formatear)
 * 
 * @param {number} value - Valor a redondear
 * @param {number} decimals - Número de decimales
 * @returns {number} - Valor redondeado
 */
export const roundNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return 0;
  
  const num = parseFloat(value);
  if (isNaN(num)) return 0;
  
  const multiplier = Math.pow(10, decimals);
  return Math.round(num * multiplier) / multiplier;
};

/**
 * Depura lo que se teclea en un campo numerico (onInput)
 * Solo digitos y un punto decimal; sin signo ni separador de miles.
 * Con decimals = 0 quita el punto; con decimals > 0 recorta los decimales
 * sobrantes; con null no recorta.
 *
 * Ejemplos:
 * sanitizeNumericInput("1,234.5a6") → "1234.56"
 * sanitizeNumericInput("1.2.3") → "1.23"
 * sanitizeNumericInput("12.345", 2) → "12.34"
 * sanitizeNumericInput("12.5", 0) → "125"
 *
 * @param {string} value - Texto capturado
 * @param {number|null} decimals - Decimales permitidos (null = sin recorte)
 * @returns {string} - Texto depurado (sin comas: se guarda tal cual)
 */
export const sanitizeNumericInput = (value, decimals = null) => {
  let valor = String(value ?? '').replace(/[^0-9.]/g, '');
  const parts = valor.split('.');
  if (parts.length > 2) {
    valor = parts[0] + '.' + parts.slice(1).join('');
  }

  if (decimals === 0) {
    return valor.replace(/\./g, '');
  }
  if (decimals > 0) {
    const [entero, decimal] = valor.split('.');
    if (decimal && decimal.length > decimals) {
      return entero + '.' + decimal.substring(0, decimals);
    }
  }
  return valor;
};